// ============================================================================
// GLOBALADVERTSMANAGER.JS - VERSION: 2026.01.13.1300
// ============================================================================
// Admin component for managing global fallback advertisements
// Each ad size has two fields: Image URL and HTML/Script (e.g., Google AdSense)
// Priority: HTML > Image URL > Nothing
// ============================================================================

import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, ExternalLink, Code, Image, AlertCircle } from 'lucide-react';

const API_URL = 'https://api.traktag.com/api';

const AD_SIZES = [
  { key: 'mobile', label: 'Mobile Banner', size: '320×50', description: 'Bottom of quiz on mobile devices' },
  { key: 'desktop', label: 'Desktop Leaderboard', size: '728×90', description: 'Top/bottom of quiz on desktop' },
  { key: 'rectangle', label: 'Rectangle', size: '300×250', description: 'In-content / sidebar' },
  { key: 'skyscraper', label: 'Skyscraper', size: '300×600', description: 'Tall sidebar / exit popup' },
  { key: 'interstitial', label: 'Interstitial', size: '320×480', description: 'Full screen between content (AdMob)' }
];

const GlobalAdvertsManager = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('ads'); // 'ads' or 'preview'

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/admin/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      setSettings(data || {});
    } catch (err) {
      console.error('Failed to load settings:', err);
      setError('Failed to load settings: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/admin/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      if (data.success) {
        setSuccess('Settings saved successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error(data.error || 'Save failed');
      }
    } catch (err) {
      console.error('Failed to save settings:', err);
      setError('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const getPreviewContent = (adKey) => {
    const html = settings[`${adKey}_html`];
    const image = settings[`${adKey}_image`];
    
    if (html) return { type: 'html', content: html };
    if (image) return { type: 'image', content: image };
    return { type: 'none', content: null };
  };

  if (loading) {
    return (
      <div className="bg-purple-800 rounded-lg p-4">
        <p className="text-purple-400 text-center py-4">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="bg-purple-800 rounded-lg p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-white font-bold text-lg flex items-center gap-2">
          📺 Global Fallback Adverts
        </h4>
        <div className="flex gap-2">
          <button
            onClick={loadSettings}
            disabled={loading}
            className="bg-purple-600 hover:bg-purple-500 text-white text-sm px-3 py-2 rounded flex items-center gap-1"
          >
            <RefreshCw size={16} /> Refresh
          </button>
          <button
            onClick={saveSettings}
            disabled={saving}
            className="bg-teal-600 hover:bg-teal-500 text-white text-sm px-3 py-2 rounded flex items-center gap-1"
          >
            <Save size={16} /> {saving ? 'Saving...' : 'Save All'}
          </button>
        </div>
      </div>

      <p className="text-purple-300 text-sm">
        These ads show when a quiz doesn't have its own specific ads configured.
        <br />
        <strong>Priority:</strong> HTML/Script code → Image URL → Nothing
      </p>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-purple-600 pb-2">
        <button
          onClick={() => setActiveTab('ads')}
          className={`px-4 py-2 rounded-t text-sm font-semibold ${
            activeTab === 'ads' 
              ? 'bg-purple-600 text-white' 
              : 'bg-purple-900 text-purple-400 hover:text-white'
          }`}
        >
          📝 Edit Ads
        </button>
        <button
          onClick={() => setActiveTab('preview')}
          className={`px-4 py-2 rounded-t text-sm font-semibold ${
            activeTab === 'preview' 
              ? 'bg-purple-600 text-white' 
              : 'bg-purple-900 text-purple-400 hover:text-white'
          }`}
        >
          👁️ Preview
        </button>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-900 border border-red-600 text-red-200 px-4 py-2 rounded flex items-center gap-2">
          <AlertCircle size={16} /> {error}
        </div>
      )}
      {success && (
        <div className="bg-green-900 border border-green-600 text-green-200 px-4 py-2 rounded">
          ✅ {success}
        </div>
      )}

      {/* Edit Tab */}
      {activeTab === 'ads' && (
        <div className="space-y-4">
          {/* Global Click URL */}
          <div className="bg-purple-900 rounded-lg p-4">
            <label className="text-teal-300 font-semibold text-sm block mb-2">
              🔗 Global Click URL (for image ads)
            </label>
            <input
              type="url"
              value={settings.fallback_click_url || ''}
              onChange={(e) => updateSetting('fallback_click_url', e.target.value)}
              placeholder="https://traktag.com/advertise"
              className="w-full bg-purple-700 text-white px-3 py-2 rounded text-sm"
            />
            <p className="text-purple-400 text-xs mt-1">
              All image banner clicks go here. HTML ads handle their own click tracking.
            </p>
          </div>

          {/* Ad Sizes */}
          {AD_SIZES.map(ad => (
            <div key={ad.key} className="bg-purple-900 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-start">
                <div>
                  <h5 className="text-white font-bold">{ad.label}</h5>
                  <p className="text-purple-400 text-xs">{ad.size} • {ad.description}</p>
                </div>
                {getPreviewContent(ad.key).type !== 'none' && (
                  <span className={`text-xs px-2 py-1 rounded ${
                    getPreviewContent(ad.key).type === 'html' 
                      ? 'bg-teal-600 text-white' 
                      : 'bg-blue-600 text-white'
                  }`}>
                    {getPreviewContent(ad.key).type === 'html' ? '📜 HTML Active' : '🖼️ Image Active'}
                  </span>
                )}
              </div>

              {/* Image URL Field */}
              <div>
                <label className="text-purple-300 text-xs flex items-center gap-1 mb-1">
                  <Image size={12} /> Image URL:
                </label>
                <input
                  type="url"
                  value={settings[`${ad.key}_image`] || ''}
                  onChange={(e) => updateSetting(`${ad.key}_image`, e.target.value)}
                  placeholder={`https://example.com/ads/${ad.key}-${ad.size.replace('×', 'x')}.png`}
                  className="w-full bg-purple-700 text-white px-3 py-2 rounded text-sm"
                />
              </div>

              {/* HTML/Script Field */}
              <div>
                <label className="text-purple-300 text-xs flex items-center gap-1 mb-1">
                  <Code size={12} /> HTML / AdSense Code: 
                  <span className="text-teal-400">(takes priority over image)</span>
                </label>
                <textarea
                  value={settings[`${ad.key}_html`] || ''}
                  onChange={(e) => updateSetting(`${ad.key}_html`, e.target.value)}
                  placeholder={`<ins class="adsbygoogle" style="display:inline-block;width:${ad.size.split('×')[0]}px;height:${ad.size.split('×')[1]}px" data-ad-client="ca-pub-XXXXXXXX" data-ad-slot="XXXXXXXX"></ins>`}
                  rows={3}
                  className="w-full bg-purple-700 text-white px-3 py-2 rounded text-sm font-mono"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Tab */}
      {activeTab === 'preview' && (
        <div className="space-y-4">
          <div className="bg-purple-900 rounded-lg p-4">
            <p className="text-purple-300 text-sm mb-4">
              Preview shows what will display when no quiz-specific ads are set.
              <br />
              <span className="text-yellow-400">⚠️ HTML/Script ads may not render in this preview due to security restrictions.</span>
            </p>

            {AD_SIZES.map(ad => {
              const preview = getPreviewContent(ad.key);
              return (
                <div key={ad.key} className="mb-6 pb-4 border-b border-purple-700 last:border-0">
                  <div className="flex justify-between items-center mb-2">
                    <h5 className="text-white font-bold">{ad.label} ({ad.size})</h5>
                    <span className={`text-xs px-2 py-1 rounded ${
                      preview.type === 'html' ? 'bg-teal-600' : 
                      preview.type === 'image' ? 'bg-blue-600' : 'bg-gray-600'
                    } text-white`}>
                      {preview.type === 'html' ? '📜 HTML' : 
                       preview.type === 'image' ? '🖼️ Image' : '❌ Not Set'}
                    </span>
                  </div>
                  
                  <div className="bg-purple-950 rounded p-2 min-h-[60px] flex items-center justify-center">
                    {preview.type === 'none' && (
                      <p className="text-purple-500 text-sm">No ad configured</p>
                    )}
                    {preview.type === 'image' && (
                      <img 
                        src={preview.content} 
                        alt={ad.label}
                        className="max-w-full h-auto"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    )}
                    {preview.type === 'image' && (
                      <p className="text-red-400 text-sm hidden">Failed to load image</p>
                    )}
                    {preview.type === 'html' && (
                      <div className="text-center">
                        <p className="text-teal-400 text-sm">HTML/Script configured</p>
                        <code className="text-purple-400 text-xs block mt-1 max-w-md truncate">
                          {preview.content.substring(0, 100)}...
                        </code>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-purple-900 rounded-lg p-4">
        <h5 className="text-teal-300 font-semibold text-sm mb-2">💡 Google AdSense Integration</h5>
        <p className="text-purple-300 text-xs mb-2">
          To use Google AdSense as your fallback, paste the ad unit code into the HTML field:
        </p>
        <pre className="bg-purple-950 text-purple-300 text-xs p-2 rounded overflow-x-auto">
{`<ins class="adsbygoogle"
     style="display:inline-block;width:320px;height:50px"
     data-ad-client="ca-pub-6253977338779444"
     data-ad-slot="YOUR_SLOT_ID"></ins>
<script>(adsbygoogle = window.adsbygoogle || []).push({});</script>`}
        </pre>
      </div>
    </div>
  );
};

export default GlobalAdvertsManager;
